import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Container,
  useMediaQuery,
  useTheme,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  QrCode as QrCodeIcon,
  Label as StickerIcon,
  CreditCard as CardIcon,
  CurrencyRupee as CurrencyIcon,
} from '@mui/icons-material';

const CostCalculatorPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = useState({
    stands: '',
    stickers: '',
    cards: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Only allow non-negative numbers
    const numValue = value === '' ? '' : Math.max(0, parseInt(value) || 0);
    setFormData((prev) => ({
      ...prev,
      [name]: numValue === '' ? '' : numValue.toString(),
    }));
  };

  const calculate = () => {
    const standCount = parseInt(formData.stands) || 0;
    const stickerQty = parseInt(formData.stickers) || 0;
    const cardQty = parseInt(formData.cards) || 0;

    // Stand cost: ₹85 per stand
    const standCost = standCount * 85;

    // Stickers: 9 per sheet, ₹50 per sheet
    const stickerSheets = Math.ceil(stickerQty / 9);
    const stickerCost = stickerQty > 0 ? stickerSheets * 50 : 0;
    const stickerRemain =
      stickerQty > 0 && stickerQty % 9 !== 0 ? 9 - (stickerQty % 9) : 0;

    // Visiting Cards: 30 per sheet, ₹20 per sheet
    const cardSheets = Math.ceil(cardQty / 30);
    const cardCost = cardQty > 0 ? cardSheets * 20 : 0;
    const cardRemain =
      cardQty > 0 && cardQty % 30 !== 0 ? 30 - (cardQty % 30) : 0;

    // Fixed cutting cost
    const cuttingCost = 85;

    const total = standCost + stickerCost + cardCost + cuttingCost;

    return {
      standCount,
      standCost,
      stickerQty,
      stickerSheets,
      stickerCost,
      stickerRemain,
      cardQty,
      cardSheets,
      cardCost,
      cardRemain,
      cuttingCost,
      total,
    };
  };

  const results = calculate();

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 3 } }}>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 4 },
          borderRadius: 2,
        }}
      >
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <CalculateIcon sx={{ fontSize: { xs: 40, sm: 48 }, color: 'primary.main', mb: 1 }} />
          <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" gutterBottom>
            QR Print Cost Calculator
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Calculate the cost for QR stands, stickers, and visiting cards
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Table Stand Count"
              name="stands"
              type="number"
              value={formData.stands}
              onChange={handleChange}
              placeholder="Enter number of stands"
              helperText="₹85 per stand"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <QrCodeIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="body2" color="text.secondary">
                      stands
                    </Typography>
                  </InputAdornment>
                ),
              }}
              inputProps={{ min: 0 }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Sticker Quantity"
              name="stickers"
              type="number"
              value={formData.stickers}
              onChange={handleChange}
              placeholder="Enter number of stickers"
              helperText="9 per sheet, ₹50 per sheet"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <StickerIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="body2" color="text.secondary">
                      stickers
                    </Typography>
                  </InputAdornment>
                ),
              }}
              inputProps={{ min: 0 }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Visiting Card Quantity"
              name="cards"
              type="number"
              value={formData.cards}
              onChange={handleChange}
              placeholder="Enter number of visiting cards"
              helperText="30 per sheet, ₹20 per sheet"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CardIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="body2" color="text.secondary">
                      cards
                    </Typography>
                  </InputAdornment>
                ),
              }}
              inputProps={{ min: 0 }}
              variant="outlined"
            />
          </Grid>

          {/* Results Section */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Paper
              elevation={0}
              sx={{
                p: 3,
                bgcolor: 'grey.50',
                borderRadius: 2,
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CurrencyIcon color="primary" />
                Cost Breakdown
              </Typography>

              {results.standCount > 0 && (
                <Box sx={{ mt: 2, mb: 1.5 }}>
                  <Typography variant="body1" fontWeight="medium">
                    Stand Cost: ₹{results.standCost}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {results.standCount} stand(s) × ₹85
                  </Typography>
                </Box>
              )}

              {results.stickerQty > 0 && (
                <Box sx={{ mt: 2, mb: 1.5 }}>
                  <Typography variant="body1" fontWeight="medium">
                    Sticker Cost ({results.stickerSheets} sheet{results.stickerSheets > 1 ? 's' : ''}): ₹{results.stickerCost}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {results.stickerQty} sticker(s) × {results.stickerSheets} sheet(s) × ₹50
                  </Typography>
                  {results.stickerRemain > 0 ? (
                    <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                      💡 Add {results.stickerRemain} more sticker(s) to complete a full sheet
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>
                      ✔ Full sticker sheet covered
                    </Typography>
                  )}
                </Box>
              )}

              {results.cardQty > 0 && (
                <Box sx={{ mt: 2, mb: 1.5 }}>
                  <Typography variant="body1" fontWeight="medium">
                    Visiting Card Cost ({results.cardSheets} sheet{results.cardSheets > 1 ? 's' : ''}): ₹{results.cardCost}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {results.cardQty} card(s) × {results.cardSheets} sheet(s) × ₹20
                  </Typography>
                  {results.cardRemain > 0 ? (
                    <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                      💡 Add {results.cardRemain} more card(s) to complete a full sheet
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>
                      ✔ Full visiting card sheet covered
                    </Typography>
                  )}
                </Box>
              )}

              <Box sx={{ mt: 2, mb: 1.5 }}>
                <Typography variant="body1" fontWeight="medium">
                  Cutting Cost: ₹{results.cuttingCost}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Fixed cost
                </Typography>
              </Box>

              {results.total > 0 && (
                <Box
                  sx={{
                    mt: 3,
                    pt: 2,
                    borderTop: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="h5" fontWeight="bold" color="primary.main">
                    Total Cost: ₹{results.total}
                  </Typography>
                </Box>
              )}

              {results.total === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                  Enter quantities above to calculate cost
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default CostCalculatorPage;
